'use client';

import { PropsWithChildren, useContext, useEffect } from 'react';

import { locales } from '@monoimin/constants';

import { getLocalePrefix } from '../utils';

import { AlternatePageContext, defaultState } from './AlternatePageProvider';

import type { Locales, NonIntlFolders } from '@monoimin/sb-types';

type Alternate = {
  slug?: string;
  locale: Locales;
};

type AlternatePageMediatorProps = PropsWithChildren<{
  folder?: string;
  locale: Locales;
  allAlternates: Alternate[];
  isHomePage?: boolean;
}>;

/**
 * @param folder – родительский сегмент статьи, blog | cases | insider | experts и тд, может быть пустым
 * @param alternate - Объект ссылки на альтернативную версию (Storyblok Multilink)
 * @param locale - локаль текущей страницы
 * @returns
 */
export const AlternatePageMediator = ({
  children,
  folder,
  allAlternates,
  locale,
  isHomePage,
}: AlternatePageMediatorProps) => {
  const [state, setState] = useContext(AlternatePageContext);

  const { nonIntlFolders } = state;

  useEffect(() => {
    const hasState = typeof setState === 'function';
    if (!hasState) return;

    if (isHomePage) {
      setState({
        alternates: { en: '/', ru: '/ru', de: '/de', tr: '/tr', es: '/es' },
        nonIntlFolders,
      });
      return;
    }

    const newAlters = createMediatorState({
      folder,
      allAlternates,
      nonIntlFolders,
    });

    setState({ alternates: newAlters, nonIntlFolders });

    return () => {
      setState(defaultState);
    };
  }, [folder, locale, setState]);

  return children;
};

export default AlternatePageMediator;

type SlugByLocale = { [k in Locales]: string };

function createMediatorState({
  allAlternates: alternates,
  folder,
  nonIntlFolders,
}: {
  allAlternates: Alternate[];
  nonIntlFolders: NonIntlFolders;
  folder?: string;
}) {
  const alternateSlugByLocale = alternates.reduce<SlugByLocale>(
    (acc, alter) => ({ ...acc, [alter.locale]: alter.slug }),
    defaultState.alternates
  );

  return locales.reduce<SlugByLocale>((acc, locale) => {
    const localePrefix = getLocalePrefix(locale);

    const folderPrefix = getFolderPrefix(locale, nonIntlFolders, folder);

    const alternateSlug = alternateSlugByLocale[locale];
    if (!alternateSlug) {
      return { ...acc, [locale]: `${localePrefix}${folderPrefix}` || `/` };
    }

    const fullAlternatePathname = `${localePrefix}${folderPrefix}/${alternateSlug}`;

    return { ...acc, [locale]: fullAlternatePathname };
  }, defaultState.alternates);
}

function getFolderPrefix(
  locale: Locales,
  nonIntlFolders: NonIntlFolders,
  folder?: string
) {
  if (!folder) return '';

  // folder prop can be `folder/subfolder`
  const firstSegment = folder.split('/')[0] || '';
  const isNonIntlFolder = nonIntlFolders[locale][firstSegment];
  if (isNonIntlFolder) {
    return '';
  }
  return `/${folder}`;
}
